import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, KeyRound, LogOut, Plug, Shield } from 'lucide-react';

interface UserAccountMenuProps {
  userData?: {
    name?: string | null;
    email?: string | null;
    company?: string | null;
    role?: string | null;
    is_admin?: boolean | null;
    has_password?: boolean | null;
  } | null;
  isExpanded: boolean;
  onLogout: () => void;
  onChangePassword?: () => void;
  onOpenAdmin?: () => void;
  /** Opens the connect page (`/connect`): how to add BARK to an AI assistant.
   *  Offered to every account. */
  onOpenConnect?: () => void;
  onOpenChange?: (isOpen: boolean) => void;
}

function fallbackNameFromEmail(email?: string | null): string {
  if (!email) return 'User';
  const localPart = email.split('@')[0]?.trim();
  if (!localPart) return 'User';

  return localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function initialsFor(name: string, email?: string | null): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  if (parts.length === 1 && parts[0] !== 'User') {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return (email?.trim().charAt(0) || 'U').toUpperCase();
}

export default function UserAccountMenu({
  userData,
  isExpanded,
  onLogout,
  onChangePassword,
  onOpenAdmin,
  onOpenConnect,
  onOpenChange,
}: UserAccountMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const email = userData?.email?.trim() || '';
  const displayName = userData?.name?.trim() || fallbackNameFromEmail(email);
  const initials = initialsFor(displayName, email);
  const company = userData?.company?.trim();
  const role = userData?.role?.trim();
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  return (
    <div ref={menuRef} className="relative" data-testid="account-menu-root">
      <button
        ref={triggerRef}
        type="button"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-testid="account-menu-trigger"
        onClick={() => setIsOpen((current) => !current)}
        className={`w-full h-10 flex items-center rounded-md border transition-all duration-300 ${
          isOpen
            ? 'bg-primary-tint text-primary border-primary-border'
            : 'border-transparent text-fg-secondary hover:bg-primary hover:text-on-primary hover:border-primary'
        } ${isExpanded ? 'px-1.5 gap-2.5' : 'justify-center px-0'}`}
      >
        <span
          className={`rounded-full bg-primary text-on-primary flex-shrink-0 flex items-center justify-center font-semibold transition-all duration-300 ${
            isExpanded ? 'w-8 h-8 text-[11px]' : 'w-5 h-5 text-[8px]'
          }`}
        >
          {initials}
        </span>
        <span
          className={`min-w-0 text-left transition-all duration-300 ${
            isExpanded ? 'opacity-100 flex-1' : 'opacity-0 w-0 overflow-hidden'
          }`}
        >
          <span className="block truncate text-xs font-semibold text-current">
            {displayName}
          </span>
          {email && (
            <span className="block truncate text-[10px] font-normal text-current opacity-70">
              {email}
            </span>
          )}
        </span>
        <ChevronDown
          className={`h-3.5 flex-shrink-0 transition-all duration-300 ${
            isOpen ? 'rotate-180' : ''
          } ${isExpanded ? 'w-3.5 opacity-70' : 'w-0 opacity-0 overflow-hidden'}`}
          strokeWidth={1.5}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Account menu"
          data-testid="account-menu"
          className="absolute bottom-full left-0 mb-2 w-64 overflow-hidden rounded-lg border border-border-default bg-surface shadow-bark-md z-50"
        >
          <div className="p-3 border-b border-divider">
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-9 h-9 rounded-full bg-primary text-on-primary flex-shrink-0 flex items-center justify-center text-xs font-semibold">
                {initials}
              </span>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-fg">{displayName}</div>
                {email && <div className="truncate text-xs text-fg-tertiary">{email}</div>}
              </div>
            </div>
            {(company || role) && (
              <div className="mt-2 flex items-center gap-2 text-[10px] text-fg-tertiary">
                {company && <span className="truncate">{company}</span>}
                {company && role && <span aria-hidden="true">/</span>}
                {role && <span className="truncate capitalize">{role}</span>}
              </div>
            )}
          </div>

          <div className="p-1.5">
            {onOpenConnect && (
              <button
                type="button"
                role="menuitem"
                data-testid="account-connect-action"
                onClick={() => {
                  setIsOpen(false);
                  onOpenConnect();
                }}
                className="w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-fg hover:bg-bg-secondary transition-colors"
              >
                <Plug className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" />
                <span>Connect your AI assistant</span>
              </button>
            )}
            {onChangePassword && userData?.has_password !== false && (
              <button
                type="button"
                role="menuitem"
                data-testid="account-change-password-action"
                onClick={() => {
                  triggerRef.current?.focus();
                  setIsOpen(false);
                  onChangePassword();
                }}
                className="w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-fg hover:bg-bg-secondary transition-colors"
              >
                <KeyRound className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" />
                <span>Change password</span>
              </button>
            )}
            {userData?.is_admin && onOpenAdmin && (
              <button
                type="button"
                role="menuitem"
                data-testid="account-admin-action"
                onClick={() => {
                  setIsOpen(false);
                  onOpenAdmin();
                }}
                className="w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-fg hover:bg-bg-secondary transition-colors"
              >
                <Shield className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" />
                <span>Admin</span>
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              data-testid="account-sign-out-action"
              onClick={handleLogout}
              className="w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-danger hover:bg-danger-bg transition-colors"
            >
              <LogOut className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
